import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { session } from 'services/session';
import { dateService } from 'services/date';
import { ratesService } from './rates-service';

import { Loader } from 'components/misc/loader-utils';
import { formatValue, handleImageError } from 'components/base/common/utils';

import RetailerBio from './retailer-bio';
import RetailerPerformance from './retailer-performance';
import OverviewTable from './overview-table';
import StrategiesDropdown from './strategies-dropdown';
import DatePicker from '../../base/date/date-picker';
import SortbyDropdown from './sortby-dropdown';
import ConfirmDropdown from './confirm-dropdown';
import SearchBar from './search-bar';

import breadCrumbArrow from 'images/icons/breadcrumb-arrow.svg';
import placeholderImage from 'images/logo_placeholder.png';

import './retailer-profile.scss';

const mockResponse = () => {
	const pricingModel = [1, 2, 4, 5][Math.floor(Math.random() * 4)];
	const publisherEPC = parseFloat(Math.random()).toFixed(2);
	const clicks = Math.floor(Math.random() * 10000);
	const publisherTotalEarnings = (parseFloat(publisherEPC) * clicks).toFixed(2);
	const strategies = [];
	const feeTypes = ['marketplace', 'direct_deal'];
	const strategyStatuses = ['upcoming', 'expired', 'running_and_active', 'running_and_paused'];

	const strategiesLength = Math.floor(Math.random() * 10 + 100);
	for (let i = 0; i < strategiesLength; i++) {
		const epc = parseFloat(Math.random()).toFixed(2);
		const clicks = Math.floor(Math.random() * 10000);

		const startOffset = Math.floor(Math.random() * 90) * [1, -1][Math.round(Math.random())];
		const endOffset = startOffset + Math.random() * 90;
		const now = dateService.getDateTime();
		const start = dateService.offsetFromDate(now, startOffset);
		const end = dateService.offsetFromDate(now, endOffset);
		const currentStrategyStatus = startOffset > 0 ? strategyStatuses[0] :
			dateService.getCurrentDateTime() > end ? strategyStatuses[1] :
				strategyStatuses.slice(2)[Math.floor(Math.random() * (strategyStatuses.length - 2))];

		strategies.push({
			strategy_id: 1000 + i,
			strategy_name: `Strategy ${i}`,
			merchant_name: 'Moda Operandi',
			commission_rate: pricingModel === 2 || pricingModel === 4
				? String(Math.floor(Math.random() * 100))
				: null,
			strategy_bid: pricingModel === 1
				? parseFloat(Math.random()).toFixed(2)
				: null,
			publisher_total_earnings: (parseFloat(publisherEPC) * clicks).toFixed(2),
			publisher_epc: epc,
			clicks: clicks,
			visible_to: [Math.round(Math.random() * (1051 - 1049) + 1049)],
			merch_id: Math.floor(Math.random() * 10 + 1000),
			strategy_is_enabled: [true, false][Math.round(Math.random())],
			strategy_fee_type: feeTypes[Math.floor(Math.random() * feeTypes.length)],
			strategy_datetime_start: dateService.toIso8601UtcDatetime(start),
			strategy_datetime_end: dateService.toIso8601UtcDatetime(end),
			strategy_status: currentStrategyStatus,
		});
	}

	return {
		merch_id: 1000,
		slug: 'modaoperandi',
		merchant_name: 'Moda Operandi',
		logo_url: 'https://static.bam-x.com/assets/logos/merchants/modaoperandi.png',
		marketplace_epc: parseFloat(Math.random()).toFixed(2),
		commission_rate: pricingModel !== 2 && pricingModel !== 4
			? String(Math.floor(Math.random() * 100))
			: parseFloat(Math.random()).toFixed(2),
		pricing_model: pricingModel,
		publisher_epc: publisherEPC,
		clicks,
		publisher_total_earnings: publisherTotalEarnings,
		mock_strategies: strategies,
		is_in_network: !!Math.floor(Math.random() * 2),
		strategies: {
			num_strategies: Math.floor(Math.random() * 10),
			pricing_model: [[1, 2, 4, 5][Math.floor(Math.random() * 4)]],
			top_commission_rate: Math.floor(Math.random() * 5).toFixed(2),
			top_publisher_commission_rate: Math.floor(Math.random() * 5).toFixed(2),
		},
		bio: (
			`At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium
			voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint
			occaecati cupiditate non provident, similique sunt in culpa qui officia deserunt mollitia
			animi, id est laborum et dolorum fuga. Et harum quidem rerum facilis est et expedita
			distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit
			quo minus id quod maxime placeat facere possimus, omnis voluptas assumenda est, omnis
			dolor repellendus. Temporibus autem quibusdam et aut officiis debitis aut rerum
			necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae.
			Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus
			maiores alias consequatur aut perferendis doloribus asperiores repellat.`
		),
		sale_items_count: String(Math.floor(Math.random() * 100)),
	};
};

// SortBy Dropdown Options
const sortOptions = [
	{label: 'Strategy Rate', value: 'strategy_rate', direction: 'desc'},
	{label: 'Your Clicks', value: 'clicks', direction: 'desc'},
	{label: 'Your Earnings', value: 'publisher_total_earnings', direction: 'desc'},
	{label: 'Your EPC', value: 'publisher_epc', direction: 'desc'},
];

const detailsDropdown = [
	{label: 'Commission', value: 'strategy_rate'},
	{label: 'CPC', value: 'strategy_bid'},
	{label: 'Date Expected', value: 'strategy_datetime_start'},
	{label: 'Date Expires', value: 'strategy_datetime_end'},
];

const headerSortOptions = [
	{
		label: 'Details',
		value: 'strategy_rate',
		direction: 'desc',
		dropdown: detailsDropdown,
	},
	{label: 'Strategy', value: 'strategy_name', direction: 'asc'},
	{label: 'Type', value: 'strategy_fee_type', direction: 'asc'},
	{label: 'Your Clicks', value: 'clicks', direction: 'desc'},
	{label: 'Your Earnings', value: 'publisher_total_earnings', direction: 'desc'},
	{label: 'Your EPC', value: 'publisher_epc', direction: 'desc'},
];

// Payment Model Options
const paymentModels = {
	1: {label: 'CPC', descriptors: ['Flat EPC']}, // CPC
	2: {label: 'CPA', descriptors: ['Last-click 1-7 days']}, // CPA
	4: {label: 'CPA+', descriptors: ['Post-click 30', 'No returns']}, // EPP
	5: {label: 'Dynamic EPC', descriptors: ['Post-click 30', 'No returns']}, // ???
};

// Campaign Label Dropdown Options
const campaignLabelOptions = [
	{label: 'Product Launch', value: '1', selected: true},
	{label: 'Sale', value: '2', selected: true},
	{label: 'Vendor Funded', value: '3', selected: true},
	{label: 'Share of Voice', value: '4', selected: true},
	{label: 'Preferred Publisher', value: '5', selected: true},
	{label: 'Growth Incentive', value: '6', selected: true},
];

const feeTypeOptions = [
	{label: 'Marketplace (Standard)', value: 'marketplace', selected: true},
	{label: 'Direct Deal', value: 'direct_deal', selected: true},
];

// Table Column Headers
const columnHeaders = [
	['Strategy'], ['Type'], ['Details'], ['Your Earnings', 'Your Clicks', 'Your EPC'],
];

// Strategies Table Tabs
const strategiesTableTabs = [
	{label: 'All Strategies', value: 'strategies'},
	{label: 'Coming Soon', value: 'upcoming strategies'},
];

const RetailerProfile = ({match}) => {
	const dateRanges = ratesService.getDateRanges();
	const retailerId = match.params.retailerId;

	const [didMount, setDidMount] = useState(false);
	const [loading, setLoading] = useState({profile: true, overviewTable: true});
	const [showBio, setShowBio] = useState(true);
	const [tableTab, setTableTab] = useState(strategiesTableTabs[0]);

	const [retailer, setRetailer] = useState(null);
	const [strategies, setStrategies] = useState(null);
	const [mockData, setMockData] = useState(false);


	const [sortBy, setSortBy] = useState(session.isAdminView ? headerSortOptions[0] : sortOptions[0]);
	const [headerSort, setHeaderSort] = useState(headerSortOptions[0].dropdown[0]);
	const [currentHeader, setCurrentHeader] = useState(sortBy.label);
	const [dateRange, setDateRange] = useState(session.isAdminView
		? ratesService.createDateRangeState(dateService.getPresetDateRanges().thirtyDay)
		: dateRanges[1]);
	const [filterBy, setFilterBy] = useState(ratesService.strategyOptions[1]);
	const [campaignLabels, setCampaignLabel] = useState(campaignLabelOptions);
	const [feeTypes, setFeeType] = useState(feeTypeOptions);
	const [searchQuery, setSearchQuery] = useState('');
	const [params, setParams] = useState({});
	const [searchTerm, setSearchTerm] = useState('');

	// Active Status
	const activeStatus = retailer && retailer.is_in_network;
	const participatingTxt = tableTab.value === 'strategies' && strategies && !loading.overviewTable
		? ratesService.getParticipatingDisplayTxt(strategies, false)
		: null;

	useEffect(() => {
		setDidMount(true);
	}, []);

	useEffect(() => {
		setParams((prevState) => {
			return {
				...prevState,
				date_from: dateService.formatDate(dateRange.startDate),
				date_to: dateService.formatDate(dateRange.endDate),
				merch_id: retailerId,
				strategy_status: filterBy.value !== 'all' ? filterBy.value : null,
				order_by: sortBy.value,
				direction: sortBy.direction,
				campaign_label: ratesService.getSelectedCampaignLabels(campaignLabels),
				fee_types: ratesService.getSelectedFeeType(feeTypes),
				page: 1,
				per_page: 500,
			};
		});
	}, [dateRange, sortBy, campaignLabels, feeTypes, filterBy]);

	useEffect(() => {
		if (didMount) {
			if (params.hasOwnProperty('search_type')) {
				setLoadState('overviewTable', true);
			} else {
				setLoadState('profile', true);
			}
			getRetailer();
		}
	}, [params, mockData]);

	useEffect(() => {
		if (didMount) {
			if (searchQuery) {
				setParams((prevState) => {
					return {
						...prevState,
						search_type: 'strategy_name',
						search_query: searchQuery,
					};
				});
			} else {
				clearSearchParams();
			}
		}
	}, [searchQuery]);

	useEffect(() => {
		const sortDropdownOptions = session.isAdminView ? headerSortOptions : sortOptions;
		const tabSortOptions = ratesService.getTabSortOptions(sortDropdownOptions, tableTab);

		if (didMount && !tabSortOptions.map((option) => option.value).includes(sortBy.value)) {
			setSortBy(tabSortOptions[0]);
		}

		if (session.isAdminView && ['strategies', 'upcoming strategies'].includes(tableTab.value)) {
			setSortBy(headerSortOptions[0]);
			setHeaderSort(headerSortOptions[0].dropdown[0]);
			setCurrentHeader(headerSortOptions[0].label);

			if (tableTab.value === 'upcoming strategies') {
				// Do not show "Date Expired" on Coming Soon tab
				headerSortOptions[0].dropdown = detailsDropdown.slice(0, -1);
			} else {
				headerSortOptions[0].dropdown = detailsDropdown;
			}
		}
	}, [tableTab]);

	function getRetailer() {
		if (mockData) {
			// Mock Performance Stats
			formatRetailer(mockResponse(), mockResponse());
			// Mock Strategies Stats
			setStrategies(mockResponse().mock_strategies);
			setLoadState('profile', false);
			setLoadState('overviewTable', false);
		} else if (params.hasOwnProperty('search_type')) {
			ratesService.getStrategies(params).then((resp) => {
				const strategyStats = resp.data.data[0].stats;
				setStrategies(strategyStats);
				setLoadState('overviewTable', false);
			});
		} else {
			const ratesParams = {...params};
			const strategiesOrderBy = [
				'strategy_rate', 'strategy_datetime_start', 'strategy_datetime_end',
				'strategy_name', 'strategy_fee_type',
			];
			if (strategiesOrderBy.includes(ratesParams.order_by)) {
				delete ratesParams.order_by;
			}
			const previousParams = {
				...ratesParams,
				date_from: dateService.formatDate(dateRange.previousStartDate),
				date_to: dateService.formatDate(dateRange.previousEndDate),
			};
			Promise.all([
				ratesService.getRates(previousParams),
				ratesService.getRates(ratesParams),
				ratesService.getStrategies(params),
			]).then(([prevStats, stats, strategies]) => {
				formatRetailer(prevStats.data.data[0].stats[0], stats.data.data[0].stats[0]);
				const strategyStats = strategies.data.data[0].stats;
				setStrategies(strategyStats);
				setLoadState('overviewTable', false);
				setLoadState('profile', false);
			});
		}
	}

	function formatRetailer(prevStats, stats) {
		const calcChange = (prev, curr) => {
			return parseInt((((parseFloat(curr) - parseFloat(prev)) / parseFloat(curr)) || 0) * 100);
		};

		const strategyRate = stats.strategies ? stats.strategies.top_publisher_commission_rate : null;
		const strategyPricingModel = stats.strategies ? stats.strategies.pricing_model : null;

		setRetailer({
			...stats,
			// Format baseline rates and strategies column values
			commission_rate: stats.pricing_model === 1
				? `${formatValue(stats.commission_rate, 'currency')} per click`
				: `${formatValue(stats.commission_rate, 'percent', 'commission_rate')} Commission`,
			evergreen_rate: stats.pricing_model === 1
				? `${formatValue(stats.evergreen_rate, 'currency')} per click`
				: `${formatValue(stats.evergreen_rate, 'percent', 'commission_rate')} Commission`,
			payment_model: paymentModels[stats.pricing_model] || paymentModels[5],

			// Format strategy rate
			strategic_rate: strategyRate
				? strategyPricingModel && strategyPricingModel === 1
					? `${formatValue(strategyRate, 'currency')} per click`
					: `${formatValue(strategyRate, 'percent', 'commission_rate')} Commission`
				: null,

			// for CPC strategies
			strategy_bid: stats.strategy_bid
				? `${formatValue(stats.strategy_bid, 'currency')} per click`
				: null,

			// Format performance values
			publisher_epc: formatValue(stats.publisher_epc, 'currency'),
			clicks: formatValue(stats.clicks),
			publisher_total_earnings: formatValue(
				stats.publisher_total_earnings, 'currency', 'publisher_total_earnings'
			),


			// Calculate percentage performance change
			publisher_epc_change: calcChange(prevStats.publisher_epc, stats.publisher_epc),
			clicks_change: calcChange(prevStats.clicks, stats.clicks),
			publisher_total_earnings_change: calcChange(
				prevStats.publisher_total_earnings, stats.publisher_total_earnings
			),
		});
	}

	function clearSearchParams() {
		const newParams = {...params};
		if (newParams.hasOwnProperty('search_type') && newParams.hasOwnProperty('search_query')) {
			delete newParams.search_type;
			delete newParams.search_query;
			setParams(newParams);
		}
	}

	function setLoadState(type, value) {
		setLoading((prevState) => {
			return {
				...prevState,
				[type]: value,
			};
		});
	}

	const comingSoonStrats = strategies && strategies.filter((strat) => strat.strategy_status === 'upcoming');

	const tableTabs = strategiesTableTabs.map((tab, idx) => {
		const strategyCount = tab.value === 'upcoming strategies' && comingSoonStrats
			? ratesService.getVisibleDataCount(comingSoonStrats)
			: strategies && ratesService.getVisibleDataCount(strategies, false, true);
		return strategyCount ? (
			<div
				key={idx}
				className={`tab ${tab === tableTab ? 'active' : ''}`}
				onClick={() => setTableTab(strategiesTableTabs[idx])}>
				<span>
					{tab.label}
				</span>
				<span className="count">
					{strategyCount}
				</span>
			</div>
		) : null;
	});

	const retailerBio = showBio ? (
		<RetailerBio
			retailer={retailer}
			paymentModels={paymentModels}
			setShowBio={setShowBio}
			getRetailer={getRetailer}
		/>
	) : (
		<div className="rates-bio toggle">
			<button className="logo" onClick={() => setShowBio(!showBio)}>
				<img
					src={retailer.logo_url || placeholderImage}
					alt={`${retailer.merchant_name || 'retailer'} logo`}
					onError={(e) => handleImageError(e, placeholderImage)}
				/>
			</button>
		</div>
	);

	const tableStrategies = strategies && tableTab.value === 'upcoming strategies'
		? comingSoonStrats
		: strategies;

	function getDisplayTxt() {
		const dataLen = ratesService.getVisibleDataCount(tableStrategies, false, true);
		const displayTxt = !dataLen
			? 'No'
			: `Displaying ${ratesService.getVisibleDataCount(tableStrategies, false, true)}`;
		const units = dataLen === 1
			? tableTab.value.substring(0, tableTab.value.length - 3) + 'y'
			: tableTab.value;
		const activeVerbiage = tableTab.value === 'strategies'
			&& filterBy.value !== 'all'
			? ` ${filterBy.value} `
			: ' ';
		return <h1>{displayTxt}{activeVerbiage}{units}</h1>;
	}

	const sortDropdownOptions = session.isAdminView ? headerSortOptions : sortOptions;
	const overviewTable = !loading.overviewTable ? (
		<OverviewTable
			data={tableStrategies}
			columnHeaders={columnHeaders}
			tab={tableTab.value}
			sortOptions={ratesService.getTabSortOptions(sortDropdownOptions, tableTab, ['strategy_rate'])}
			sortBy={sortBy}
			setSortBy={setSortBy}
			headerSort={headerSort}
			setHeaderSort={setHeaderSort}
			currentHeader={currentHeader}
			setCurrentHeader={setCurrentHeader}
			overview={false}
			retailer={retailer.merchant_name}
		/>
	) : null;

	const mocksButton = session.isAdminView ? (
		<button onClick={() => setMockData(!mockData)}>Mocks</button>
	) : null;

	const strategiesDropdown = session.isAdminView && tableTab.value === 'strategies' ? (
		<StrategiesDropdown
			strategyOptions={ratesService.strategyOptions}
			filterBy={filterBy}
			setFilterBy={setFilterBy}
		/>
	) : null;

	const campaignLabelDropdown = (
		<div className="campaign-label-dropdown">
			<ConfirmDropdown
				items={campaignLabels}
				updateItems={setCampaignLabel}
				itemType="campaign-labels"
			/>
		</div>
	);

	const feeTypesDropdown = (
		<div className="fee-type-dropdown">
			<ConfirmDropdown
				items={feeTypes}
				updateItems={setFeeType}
				itemType="fee-types"
			/>
		</div>
	);

	const datePickerDropdown = tableTab.value !== 'upcoming strategies' && params.date_from && params.date_to ?
		(
			<DatePicker
				eventPrefix="Retailer Profile Date Picker"
				dateRange={ratesService.getDateRange(params)}
				setDateRange={(startDate, endDate) => setDateRange(
					ratesService.createDateRangeState({startDate, endDate})
				)}
			/>
		) : null;

	const sortByDropdown = !session.isAdminView ? (
		<SortbyDropdown
			sortOptions={ratesService.getTabSortOptions(sortOptions, tableTab)}
			sortBy={sortBy}
			setSortBy={setSortBy}
			label={true}
		/>
	) : null;

	const activeBanner = session.isAdminView && !activeStatus && retailer ? (
		<div className={`active-banner ${!showBio ? 'hidden-bio' : ''}`}>
			<div className="caution-line"></div>
			<div className="banner-text">
				<span>{`${retailer.merchant_name} is Inactive`}</span>
				<span>
					{`You will not generate earnings by sending traffic to ${retailer.merchant_name},
						and they will be removed from multi-matched links.`}
				</span>
			</div>
		</div>
	) : null;

	const upperBanner = !showBio ? activeBanner : null;
	const lowerBanner = showBio ? activeBanner : null;

	const retailerProfile = !loading.profile ? (
		<>
			<div className="navigation">
				<Link to={`/v2/publisher/${session.orgId}/new-recs/rates`}>All Partners</Link>
				<img src={breadCrumbArrow} alt="arrow" className="breadcrumb-arrow" />
				<span className="retailer-name">{retailer.merchant_name}</span>
				{mocksButton}
			</div>

			{upperBanner}
			<div className={`content ${showBio ? 'collapse' : 'expand'}`}>
				{retailerBio}

				<div className="performance-and-strategies">
					<div className="performance">
						{lowerBanner}
						<div className="section-header">
							<h2>Your Performance</h2>

							{datePickerDropdown}
						</div>

						<RetailerPerformance
							retailer={retailer}
							dateRange={dateRange}
						/>
					</div>

					<div className="strategies-container">
						<div className="section-header">
							<div className="tabs">
								{tableTabs}
							</div>

							<div className="dropdowns">
								{strategiesDropdown}
								{campaignLabelDropdown}
								{feeTypesDropdown}
								{sortByDropdown}
							</div>
						</div>

						<div className="search-bar">
							<SearchBar
								setSearchQuery={setSearchQuery}
								placeholder="Search..."
								searchTerm={searchTerm}
								setSearchTerm={setSearchTerm}
							/>
						</div>
						<div className="counts">
							{getDisplayTxt()}
							<h1>{participatingTxt}</h1>
						</div>
						{overviewTable}
					</div>
				</div>
			</div>
		</>
	) : null;

	return (
		<div className="retailer-profile-container">
			<div className={`loader ${loading.profile ? 'profile' : 'overview-table'}`}>
				<Loader loading={loading.profile || loading.overviewTable} />
			</div>

			{retailerProfile}
		</div>
	);
};

export default RetailerProfile;
